import {
  memo,
  useRef,
  useCallback,
  useLayoutEffect,
  useImperativeHandle,
  forwardRef,
  ForwardedRef,
  useState,
} from 'react';
import type { Game } from '@excelsior-works/daily-wheel';

import {
  BonanzaWheelProps,
  BonanzaWheelExposedProps,
} from './BonanzaWheel.types';
import { Box, Loader } from '@bonanza-social/moonlight';
import { logError } from '@bonanza-social/toolkit/logging';

const WHEEL_WIDTH = 320;
const WHEEL_HEIGHT = 320;

const BonanzaWheel = (
  { handleWin, getSectors, getReward }: BonanzaWheelProps,
  ref: ForwardedRef<BonanzaWheelExposedProps>,
) => {
  const [isReady, setIsReady] = useState(false);
  const wheelInstance = useRef<Game | undefined>();
  const dailyWheelElementRef = useRef<HTMLDivElement | null>(null);

  const initWheel = useCallback(async () => {
    const [BonanzaWheelLib, sectors] = await Promise.all([
      import('@excelsior-works/daily-wheel'),
      getSectors(),
    ]);

    wheelInstance.current = new BonanzaWheelLib.Game({
      selectorId: `bonanza-wheel`,
      width: WHEEL_WIDTH,
      height: WHEEL_HEIGHT,
      wheelDiameter: 302,
      hideClaimButton: true,
      debug: false,
      coinAmount: 100,
      maxFps: 60,
      versionHash: `${Math.random()}`, // TODO change this
      sectors,
      onFinish: handleWin,
      onRequestError: (error) => logError('Wheel error', error),
      onReady: () => setIsReady(true),
      getReward,
    });

    wheelInstance.current?.run();
  }, [getSectors, handleWin, getReward]);

  // On mount, initialize the Daily Wheel
  useLayoutEffect(() => {
    if (wheelInstance.current) {
      wheelInstance.current?.run();
    } else {
      initWheel();
    }

    return () => {
      wheelInstance.current?.destroy();
      wheelInstance.current = undefined;
    };
  }, [initWheel]);

  useImperativeHandle(
    ref,
    () => ({
      reset: () => wheelInstance.current?.reset(),
    }),
    [],
  );

  return (
    <Box centerContent>
      <div
        style={{ width: WHEEL_WIDTH, height: WHEEL_HEIGHT }}
        className="bonanza-wheel"
        id="bonanza-wheel"
        ref={dailyWheelElementRef}
      />
      {isReady ? null : (
        <Box absoluteFill centerContent>
          <Loader />
        </Box>
      )}
    </Box>
  );
};

export default memo(forwardRef(BonanzaWheel));
